import { useMemo, useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import { Icon } from "@core/icons";
import type { Benefit, BilingualText, CatalogEntry } from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  CreateActionButton,
  EditorFooter,
  EditorHeader,
  EditorModal,
  type EditorModalTarget,
  EmptyResultsPanel,
  FieldError,
  includesToFormValues,
  includesToInput,
  IncludesFieldGroup,
  InlineNote,
  inputStyle,
  LabeledField as BField,
  ListingTablePanel,
  placesAndValidityInput,
  PlacesFieldGroup,
  PrizesScreenToolbar,
  resolveBilingualText as text,
  RFieldGroup,
  RowActionsCell,
  RowEditButton,
  submitCreateOrEdit,
  Toggle,
  useEditorForm,
  useIncludesField,
  usePlacesToggle,
  useToast,
  ValidityRangeFields,
  type ContentLanguage,
} from "@portal/common";
import { ApiError } from "@core/http";
import {
  applyApiFieldErrors,
  reportErrorWithPrefix,
  reportUnlessFieldError,
} from "@portal/common/errorMessage";
import { useEntries } from "../catalogs/catalogQueries";
import type { BenefitInput } from "./api";
import { useBenefitMutations, useBenefits } from "./benefitQueries";
import {
  benefitFormSchema,
  FIELDS_REQUIRED_MESSAGE,
  type BenefitFormValues,
} from "./benefitSchemas";

// Ícono gallery, admin.jsx:2482 (ICONOS_BENEF) — literal, no inventado.
const BENEFIT_ICONS = [
  "gift",
  "medal",
  "star",
  "sparkle",
  "wrench",
  "car",
  "dining",
  "clock",
  "pin",
  "doc",
  "invite",
  "calendar",
  "chat",
  "bell",
  "link",
  "home",
  "send",
  "check",
] as const;

// SPEC-211 §9: los dos códigos que el servidor puede rechazar al guardar sin
// que la validación de cliente los haya interceptado antes — `code` no está
// en el formulario (PA-025-C) y `DETAIL_REQUIRED` es un respaldo si el zod
// de cliente se saltea.
// QA-MTZ-002 #9/#10: `FIELDS_REQUIRED` y `ACTIVE_STATE_CONFLICT` caían al
// genérico con correlación. `INVALID_REQUEST` NO va en este mapa: un código
// mapeado gana sobre el `userMessage` de la API (`resolveErrorMessage`), que
// es el texto que nombra la referencia inexistente — solo se usa
// `INVALID_REQUEST_FALLBACK` cuando el rechazo no trae ni campo ni texto.
const SERVER_ERROR_MESSAGES: Partial<Record<string, string>> = {
  CODE_DUPLICATE: "Ya existe un beneficio con ese código",
  DETAIL_REQUIRED: "El detalle es obligatorio: es lo que el miembro lee",
  FIELDS_REQUIRED: FIELDS_REQUIRED_MESSAGE,
  ACTIVE_STATE_CONFLICT:
    "El beneficio cambió de estado mientras lo editabas. Cierra el formulario y vuelve a abrirlo.",
};
const INVALID_REQUEST_FALLBACK =
  "Revisa los datos del beneficio: el nombre, el detalle y cada ítem de «Qué incluye» necesitan texto en español.";

// Campos que muestran su propio error bajo el input: un `errors[].field` de
// la API fuera de esta lista (p. ej. `merchantIds`, `categoryId`) cae al
// toast con el `userMessage` de la API.
const SERVER_FIELD_KEYS = [
  "nameEs",
  "detailEs",
  "serviceTypeId",
  "includes",
  "workshopIds",
  "validUntil",
] as const;

function benefitErrorMessages(error: unknown): Partial<Record<string, string>> {
  if (
    error instanceof ApiError &&
    error.code === "INVALID_REQUEST" &&
    !error.problem?.userMessage
  ) {
    return {
      ...SERVER_ERROR_MESSAGES,
      INVALID_REQUEST: INVALID_REQUEST_FALLBACK,
    };
  }
  return SERVER_ERROR_MESSAGES;
}

/** Vuelca al formulario los rechazos por campo de la API; `true` si asoció
 * al menos uno (el llamador ya no muestra toast). */
type ApplyFieldErrors = (error: unknown) => boolean;

const REDEMPTION_TYPES = [
  { value: "once", label: "Única vez" },
  { value: "monthly", label: "Mensual" },
  { value: "yearly", label: "Anual" },
] as const;

function redemptionLabel(value: Benefit["redemptionType"]) {
  return REDEMPTION_TYPES.find((type) => type.value === value)?.label ?? value;
}

// SPEC-211 §3/§9: sin `stat-gold` para "Anual" en el prototipo (admin.jsx:2541
// usa neutral/silver/gold por tipo) — literal.
function redemptionStatClass(value: Benefit["redemptionType"]) {
  if (value === "once") return "neutral";
  if (value === "monthly") return "silver";
  return "gold";
}

interface BenefitsFilterState {
  search: string;
  type: "all" | Benefit["redemptionType"];
  status: "all" | "active" | "inactive";
}
const BLANK_FILTER: BenefitsFilterState = {
  search: "",
  type: "all",
  status: "all",
};

function matchesFilter(benefit: Benefit, filter: BenefitsFilterState) {
  if (filter.type !== "all" && benefit.redemptionType !== filter.type)
    return false;
  if (filter.status === "active" && !benefit.active) return false;
  if (filter.status === "inactive" && benefit.active) return false;
  if (filter.search.trim()) {
    const needle = filter.search.trim().toLowerCase();
    const haystack =
      `${text(benefit.name, "es")} ${text(benefit.detail, "es")}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

function defaultsFor(benefit: Benefit | null): BenefitFormValues {
  if (!benefit) {
    return {
      nameEs: "",
      nameEn: "",
      detailEs: "",
      detailEn: "",
      icon: "gift",
      categoryId: "",
      redemptionType: "once",
      requiresScheduling: false,
      serviceTypeId: "",
      includes: [],
      merchantIds: [],
      workshopIds: [],
      validFrom: "",
      validUntil: "",
      active: true,
    };
  }
  return {
    nameEs: text(benefit.name, "es"),
    nameEn: text(benefit.name, "en"),
    detailEs: text(benefit.detail, "es"),
    detailEn: text(benefit.detail, "en"),
    icon: benefit.icon ?? "gift",
    // SPEC-211, hallazgo de pérdida silenciosa: sin este campo, cada edición
    // omitía `categoryId` y `benefit.service.ts:125` lo resolvía como
    // `input.categoryId ?? null` — borraba la categoría del beneficio.
    categoryId: benefit.categoryId ?? "",
    redemptionType: benefit.redemptionType,
    requiresScheduling: benefit.requiresScheduling ?? false,
    serviceTypeId: benefit.serviceTypeId ?? "",
    includes: includesToFormValues(benefit.includes),
    merchantIds: [...(benefit.merchantIds ?? [])],
    workshopIds: [...(benefit.workshopIds ?? [])],
    validFrom: benefit.validFrom ?? "",
    validUntil: benefit.validUntil ?? "",
    active: benefit.active,
  };
}

function buildInput(values: BenefitFormValues): BenefitInput {
  const name: BilingualText = {
    es: values.nameEs,
    ...(values.nameEn ? { en: values.nameEn } : {}),
  };
  const detail: BilingualText = {
    es: values.detailEs,
    ...(values.detailEn ? { en: values.detailEn } : {}),
  };
  return {
    name,
    detail,
    ...(values.icon ? { icon: values.icon } : {}),
    ...(values.categoryId ? { categoryId: values.categoryId } : {}),
    redemptionType: values.redemptionType,
    requiresScheduling: values.requiresScheduling,
    ...(values.requiresScheduling && values.serviceTypeId
      ? { serviceTypeId: values.serviceTypeId }
      : {}),
    includes: includesToInput(values.includes),
    ...placesAndValidityInput(values),
  };
}

// `BField` (etiqueta 12px 600 + slot de error) vive en
// `@portal/common/LabeledField` (PA-025-E): mismo componente palabra por
// palabra que usaba `RField` en rewards/RewardsScreen.tsx. `GPField` en
// grand-prize/GrandPrizeConfigScreen.tsx queda fuera de este movimiento
// (deuda declarada de SPEC-210, PA-025-E).

// Un `Toggle` seguido de su etiqueta textual se repite tres veces en esta
// pantalla (estado del beneficio en la fila, estado en el encabezado del
// editor, "Requiere agendamiento"): un solo helper local en vez de repetir
// la pareja Toggle+span tres veces.
function ToggleWithLabel({
  on,
  onLabel,
  offLabel,
  onChange,
  disabled = false,
  ariaLabel,
  fontSize = 12,
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  ariaLabel: string;
  fontSize?: number;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <Toggle
        on={on}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={ariaLabel}
      />
      <span
        style={{
          font: `500 ${String(fontSize)}px ${tokens.font.ui}`,
          color: on ? tokens.text.secondary : tokens.text.disabled,
        }}
      >
        {on ? onLabel : offLabel}
      </span>
    </div>
  );
}

interface BenefitEditorProps {
  benefit: Benefit | null;
  serviceTypes: CatalogEntry[] | undefined;
  serviceTypesLoading: boolean;
  canWrite: boolean;
  submitting: boolean;
  onSubmit: (
    id: string | undefined,
    input: BenefitInput,
    applyFieldErrors: ApplyFieldErrors,
  ) => void;
  onCancel: () => void;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el editor ES el diálogo. La
  // pantalla ya no lo envuelve en un `Modal`, le pasa acá el destino.
  modal: EditorModalTarget;
}

function BenefitEditor({
  benefit,
  serviceTypes,
  serviceTypesLoading,
  canWrite,
  submitting,
  onSubmit,
  onCancel,
  modal,
}: BenefitEditorProps) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const merchants = useEntries("merchants", false, "");
  const workshops = useEntries("workshops", false, "");
  const { register, handleSubmit, watch, setValue, errors, setError } =
    useEditorForm<BenefitFormValues>(defaultsFor(benefit));
  const nameEs = watch("nameEs");
  const active = watch("active");
  const redemptionType = watch("redemptionType");
  const requiresScheduling = watch("requiresScheduling");
  const includes = watch("includes");
  const merchantIds = watch("merchantIds");
  const workshopIds = watch("workshopIds");
  const originalType = benefit?.redemptionType;

  const { toggleMerchant, toggleWorkshop } = usePlacesToggle(
    merchantIds,
    workshopIds,
    (next) => setValue("merchantIds", next, { shouldDirty: true }),
    (next) => setValue("workshopIds", next, { shouldDirty: true }),
  );
  const {
    addItem: addInclude,
    removeItem: removeInclude,
    updateItem: updateInclude,
  } = useIncludesField(includes, (next) =>
    setValue("includes", next, { shouldDirty: true }),
  );

  const submit = (values: BenefitFormValues) => {
    const parsed = benefitFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof BenefitFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(benefit?.id, buildInput(parsed.data), (error) =>
      applyApiFieldErrors(
        error,
        (field, fieldError) =>
          setError(field as keyof BenefitFormValues, fieldError),
        SERVER_FIELD_KEYS,
      ),
    );
  };

  return (
    <EditorModal
      modal={modal}
      onSubmit={handleSubmit(submit)}
      header={
        <EditorHeader
          eyebrow={benefit ? "Editar beneficio" : "Nuevo beneficio"}
          title={nameEs || "Beneficio sin nombre"}
          language={language}
          onLanguageChange={setLanguage}
          statusIndicator={
            <ToggleWithLabel
              on={active}
              onLabel="Activo"
              offLabel="Inactivo"
              ariaLabel="Activo"
              onChange={(next) =>
                setValue("active", next, { shouldDirty: true })
              }
            />
          }
        />
      }
      footer={
        <EditorFooter
          onCancel={onCancel}
          action={benefit ? "canEdit" : "canCreate"}
          disabled={submitting}
          submitting={submitting}
        />
      }
    >
      {/* El `padding` del cuerpo lo pone ahora la región del `Modal`
        (`values.modalRegions.bodyPadding`, los mismos 20px 24px que tenía
        este `fieldset`): si se dejara acá quedaría duplicado. */}
      <fieldset
        disabled={!canWrite}
        style={{ border: "none", margin: 0, padding: 0 }}
      >
        <BField
          label="Nombre"
          required={language === "es"}
          error={language === "es" ? errors.nameEs?.message : undefined}
        >
          <input
            key={language}
            {...register(language === "es" ? "nameEs" : "nameEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={inputStyle}
          />
        </BField>
        <BField
          label="Detalle de lo que comprende"
          required={language === "es"}
          hint="Es lo que el miembro lee al abrir el beneficio en la app."
          error={language === "es" ? errors.detailEs?.message : undefined}
        >
          <textarea
            key={language}
            {...register(language === "es" ? "detailEs" : "detailEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
          />
        </BField>
        <RFieldGroup
          label="Tipo de consumo"
          hint="Define cada cuánto puede volver a usarse."
        >
          <div style={{ display: "flex", gap: 10 }}>
            {REDEMPTION_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() =>
                  setValue("redemptionType", type.value, {
                    shouldDirty: true,
                  })
                }
                className={
                  "chip" + (redemptionType === type.value ? " on" : "")
                }
                style={{ padding: "11px 18px", fontSize: 13 }}
              >
                {type.label}
              </button>
            ))}
          </div>
        </RFieldGroup>
        {/* SPEC-211 §4/§9: cambiar el tipo de consumo de un beneficio ya en
            uso afecta los consumos futuros, no los registrados
            (`REDEMPTION_APPLIES_FORWARD`). El formulario lo advierte de
            inmediato, sin esperar la respuesta del servidor. */}
        {benefit && originalType && redemptionType !== originalType ? (
          <InlineNote>
            El tipo nuevo aplica a consumos futuros. Los registrados no cambian.
          </InlineNote>
        ) : null}
        {/* PA-025-A (resolución humana, human-resolution-pa-025.json): sin
            equivalente en el prototipo congelado (admin.jsx:2571-2612 no
            declara requiere agendamiento ni tipo de servicio) — hueco
            resuelto por autoridad documental (SPEC-211 §3/§4.1). */}
        <RFieldGroup
          label="Requiere agendamiento"
          hint="El beneficio se presta en un taller de Teojama y exige cita previa."
        >
          <ToggleWithLabel
            on={requiresScheduling}
            onLabel="Sí"
            offLabel="No"
            ariaLabel="Requiere agendamiento"
            onChange={(next) =>
              setValue("requiresScheduling", next, { shouldDirty: true })
            }
          />
        </RFieldGroup>
        {requiresScheduling ? (
          <BField
            label="Tipo de servicio"
            required
            error={errors.serviceTypeId?.message}
          >
            <select {...register("serviceTypeId")} style={inputStyle}>
              <option value="">Selecciona un tipo de servicio</option>
              {serviceTypesLoading ? (
                <option value="" disabled>
                  Cargando…
                </option>
              ) : (
                serviceTypes?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {text(type.name, "es")}
                  </option>
                ))
              )}
            </select>
          </BField>
        ) : null}
        <RFieldGroup
          label="Ícono"
          hint="Identifica el beneficio en la lista y en su ficha dentro de la app."
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: 14,
              border: `1px dashed ${tokens.surface.line2}`,
              borderRadius: 12,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                flex: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: tokens.surface.surface2,
                border: `1px solid ${tokens.surface.line2}`,
                color: tokens.silver.flat,
              }}
            >
              <Icon name={watch("icon")} size={26} stroke={1.4} />
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                flex: 1,
              }}
            >
              {BENEFIT_ICONS.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => setValue("icon", ic, { shouldDirty: true })}
                  title={ic}
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      watch("icon") === ic
                        ? tokens.surface.surface3
                        : "transparent",
                    border: `1px solid ${watch("icon") === ic ? tokens.surface.line2 : tokens.surface.line}`,
                    color:
                      watch("icon") === ic
                        ? tokens.text.primary
                        : tokens.text.tertiary,
                  }}
                >
                  <Icon name={ic} size={18} stroke={1.4} />
                </button>
              ))}
            </div>
          </div>
        </RFieldGroup>
        <ValidityRangeFields
          validFrom={register("validFrom")}
          validUntil={register("validUntil")}
          untilError={errors.validUntil?.message}
        />
        {/* RF-BEC-04, SPEC-211 §5: lista ordenada de elementos, cada uno
            bilingüe. Sin equivalente en el prototipo congelado — hueco
            resuelto por PA-025-A con las primitivas de formulario vigentes.
            T2 (qa-bug-matrix-2026-09.md §4.1, #15/#36/#37/#38): un ítem de
            "Qué incluye" sin texto en español entra por `includeSchema`
            (benefitSchemas.ts). El error se ve acá, donde está el campo, en
            vez de viajar al servidor sin mensaje. T5 intento 2: el
            `FieldError` ahora lo dibuja `IncludesFieldGroup` (prop `error`),
            no esta pantalla — ver su comentario de cabecera. */}
        <IncludesFieldGroup
          label="Qué incluye"
          hint="Se muestra completo en la app y en el validador."
          items={includes}
          language={language}
          onAdd={addInclude}
          onRemove={removeInclude}
          onUpdate={updateInclude}
          error={errors.includes?.message}
        />
        {/* PA-025-A, ANNEX-011 §2.1 (RF-BEC-05): mismo patrón que
            rewards/RewardsScreen.tsx (PA-023-C) — comercios y talleres
            pueden coexistir ("o ambos"). */}
        <PlacesFieldGroup
          merchants={merchants}
          merchantIds={merchantIds}
          onToggleMerchant={toggleMerchant}
          workshops={workshops}
          workshopIds={workshopIds}
          onToggleWorkshop={toggleWorkshop}
        />
        {errors.workshopIds?.message ? (
          <FieldError message={errors.workshopIds.message} />
        ) : null}
        {/* SPEC-211 §6/§9 (`NO_MERCHANTS`): sin ningún lugar asociado el
            beneficio no puede consumirse — advertencia, no rechazo (a
            diferencia de requiere agendamiento sin taller, que sí rechaza,
            arriba). */}
        {!merchantIds.length && !workshopIds.length ? (
          <InlineNote>
            Este beneficio no tiene comercios habilitados y no podrá consumirse.
          </InlineNote>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 13,
            alignItems: "flex-start",
            padding: "14px 16px",
            borderRadius: 13,
            border: `1px solid ${tokens.surface.line2}`,
            background: tokens.surface.surface,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              flex: "none",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              padding: 7,
              border: `1px solid ${tokens.surface.line2}`,
              background: "#101012",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                style={{
                  borderRadius: 2,
                  background: i === 3 ? "transparent" : tokens.text.tertiary,
                }}
              />
            ))}
          </div>
          <div
            style={{
              font: `400 11.5px/1.6 ${tokens.font.ui}`,
              color: tokens.text.tertiary,
            }}
          >
            El miembro genera un{" "}
            <b style={{ color: tokens.text.secondary, fontWeight: 600 }}>
              código QR desde la app
            </b>{" "}
            para consumir este beneficio. El asesor lo escanea en el punto de
            atención y el consumo se descuenta según el tipo seleccionado.
          </div>
        </div>
      </fieldset>
    </EditorModal>
  );
}

const COLS = "minmax(230px,1.3fr) minmax(280px,2fr) 148px 128px 176px";

export function BenefitsScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const benefits = useBenefits();
  const serviceTypes = useEntries("service-types", false, "");
  const mutations = useBenefitMutations();
  const [filter, setFilter] = useState<BenefitsFilterState>(BLANK_FILTER);
  const [editing, setEditing] = useState<Benefit | null | undefined>(undefined);

  const canCreate = hasPermission("content", "canCreate");
  const canEdit = hasPermission("content", "canEdit");
  const canInactivate = hasPermission("content", "canInactivate");

  const list = benefits.data;
  const rows = useMemo(
    () => (list ?? []).filter((benefit) => matchesFilter(benefit, filter)),
    [list, filter],
  );

  const submitting = mutations.create.isPending || mutations.edit.isPending;

  const onSaved = (benefit: Benefit) => {
    setEditing(undefined);
    toast.show(`${text(benefit.name, "es")} guardado y publicado`);
    for (const warning of benefit.warnings ?? [])
      toast.show(warning.message, "neutral");
  };

  // T2 (qa-bug-matrix-2026-09.md §4.1, #15/#36/#37/#38): `if (message)`
  // descartaba en silencio cualquier código sin mapear (p. ej.
  // `INVALID_REQUEST`) — reemplazado por el resolutor compartido, que
  // siempre habla y agrega el identificador de correlación cuando no hay
  // texto mapeado.
  const onSubmit = (
    id: string | undefined,
    input: BenefitInput,
    applyFieldErrors: ApplyFieldErrors,
  ) => {
    const handleError = (error: unknown) =>
      reportUnlessFieldError(
        toast,
        error,
        applyFieldErrors,
        benefitErrorMessages(error),
      );
    submitCreateOrEdit(id, input, mutations, onSaved, handleError);
  };

  // "Operador no tiene inactivar" (SPEC-211 §8, corregido por SEC-05): mover
  // `active` en cualquier dirección exige `canInactivate` — el middleware
  // require-inactivate-for-active-change.ts no distingue dirección, así que
  // gatear la reactivación con `canEdit` ofrecía una acción que el backend
  // siempre rechazaba con 403. `canEdit` sigue alcanzando para el resto del
  // formulario, que no mueve `active`. Ocultar/deshabilitar el control acá es
  // reflejo de la interfaz, no control de acceso: el servidor sigue siendo
  // quien rechaza (ADR-011).
  // T2 (qa-bug-matrix-2026-09.md §4.4, #30/#35): `edit.mutate`/
  // `inactivate.mutate` iban sin `onError` — si el PUT fallaba (403, 409,
  // 400) el conmutador volvía solo a su posición y no se decía nada. El
  // mensaje nombra el registro que no se pudo mover.
  const toggleActive = (benefit: Benefit) => {
    const name = text(benefit.name, "es");
    if (benefit.active) {
      mutations.inactivate.mutate(benefit.id, {
        onSuccess: () => toast.show(`${name} inactivado`),
        onError: (error) =>
          reportErrorWithPrefix(toast, `No se pudo inactivar ${name}.`, error),
      });
      return;
    }
    // Reactivar reconstruye el input a partir de los mismos helpers que
    // usa el editor (`defaultsFor` + `buildInput`), en vez de repetir a mano
    // la forma de `BenefitInput` una segunda vez.
    const input = { ...buildInput(defaultsFor(benefit)), active: true };
    mutations.edit.mutate(
      { id: benefit.id, input },
      {
        onSuccess: (updated) => {
          toast.show(`${text(updated.name, "es")} activado`);
          for (const warning of updated.warnings ?? [])
            toast.show(warning.message, "neutral");
        },
        onError: (error) =>
          reportErrorWithPrefix(toast, `No se pudo reactivar ${name}.`, error),
      },
    );
  };

  const hasAnyBenefit = (list?.length ?? 0) > 0;

  return (
    <div>
      {/* TABS-A (human-resolution-tabs-pattern.json): Beneficios comparte el
        encabezado y la fila de pestañas de "Gestión de Premios"
        (PortalPrimitives.tsx:453-479), igual que /rewards y
        /prizes/configuration. */}
      <PrizesScreenToolbar
        filter={filter}
        setFilter={setFilter}
        searchPlaceholder="Buscar beneficio"
        extraFilters={
          <select
            value={filter.type}
            onChange={(event) =>
              setFilter((prev) => ({
                ...prev,
                type: event.target.value as BenefitsFilterState["type"],
              }))
            }
            style={{
              ...inputStyle,
              width: "auto",
              minWidth: 176,
              cursor: "pointer",
            }}
          >
            <option value="all">Tipo de consumo · todos</option>
            {REDEMPTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        }
        createLabel="Nuevo beneficio"
        onCreate={() => setEditing(null)}
      />

      <ListingTablePanel
        loading={benefits.isLoading}
        rows={rows}
        columns={COLS}
        headers={["Nombre", "Detalle", "Tipo de consumo", "Estado"]}
        empty={
          <EmptyResultsPanel
            icon="medal"
            title={
              hasAnyBenefit
                ? "Ningún beneficio coincide"
                : "Aún no hay beneficios"
            }
            description={
              hasAnyBenefit
                ? "Ajuste la búsqueda, el tipo de consumo o el estado."
                : "Los beneficios Star Circle aparecen en la app apenas exista uno activo."
            }
            actions={
              <CreateActionButton
                label="Nuevo beneficio"
                onClick={() => setEditing(null)}
              />
            }
          />
        }
        renderRow={(benefit) => {
          const canToggle = canInactivate;
          return (
            <div
              key={benefit.id}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 14,
                alignItems: "center",
                padding: "13px 24px",
                borderBottom: `1px solid ${tokens.surface.line}`,
                opacity: benefit.active ? 1 : 0.55,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 11,
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: tokens.surface.surface2,
                    border: `1px solid ${tokens.surface.line}`,
                    color: tokens.silver.flat,
                  }}
                >
                  <Icon name={benefit.icon ?? "gift"} size={17} stroke={1.5} />
                </div>
                <span
                  style={{
                    font: `600 12.5px/1.35 ${tokens.font.ui}`,
                    color: tokens.text.primary,
                  }}
                >
                  {text(benefit.name, "es")}
                </span>
              </div>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                {text(benefit.detail, "es")}
              </span>
              <span>
                <span
                  className={`stat stat-${redemptionStatClass(benefit.redemptionType)}`}
                >
                  {redemptionLabel(benefit.redemptionType)}
                </span>
              </span>
              <ToggleWithLabel
                on={benefit.active}
                onLabel="Activo"
                offLabel="Inactivo"
                ariaLabel="Activo"
                disabled={!canToggle}
                fontSize={11}
                onChange={() => toggleActive(benefit)}
              />
              <RowActionsCell>
                <RowEditButton
                  visible={canEdit}
                  onClick={() => setEditing(benefit)}
                />
              </RowActionsCell>
            </div>
          );
        }}
      />

      {editing !== undefined ? (
        <BenefitEditor
          key={editing?.id ?? "new"}
          benefit={editing}
          serviceTypes={serviceTypes.data}
          serviceTypesLoading={serviceTypes.isLoading}
          canWrite={canCreate || canEdit}
          submitting={submitting}
          onSubmit={onSubmit}
          onCancel={() => setEditing(undefined)}
          modal={{
            label: editing?.id ? "Editar beneficio" : "Nuevo beneficio",
            width: 660,
            onClose: () => setEditing(undefined),
          }}
        />
      ) : null}
    </div>
  );
}
